import React, { useState, useEffect, useCallback } from 'react';
import { FaFolderOpen, FaTimes, FaUpload, FaHistory, FaFileAlt } from 'react-icons/fa';

const LoadModal = ({ onClose, onLoad }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('file'); // 'file' or 'saved'

  // Load saved plans from localStorage
  useEffect(() => {
    const plans = JSON.parse(localStorage.getItem('saved-floor-plans') || '[]');
    setSavedPlans(plans);
  }, []);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-load-modal]')) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      setSelectedFile(file);
      setError('');
    } else {
      setError('Please select a valid JSON file');
      setSelectedFile(null);
    }
  };

  const handleFileLoad = async () => {
    if (!selectedFile) {
      setError('Please select a file to load');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const text = await selectedFile.text();
      const planData = JSON.parse(text);

      // Validate the plan data structure
      if (!planData.shapes && !planData.mergedStructures) {
        throw new Error('Invalid plan file format');
      }

      // Load the plan data
      if (planData.shapes) {
        localStorage.setItem('react-pixi-floor-planner-state', JSON.stringify(planData.shapes));
      }
      if (planData.mergedStructures) {
        localStorage.setItem('react-pixi-floor-planner-merged-structures', JSON.stringify(planData.mergedStructures));
      }

      onLoad && onLoad(planData);
      onClose();
    } catch (error) {
      console.error('Error loading plan:', error);
      setError('Error loading plan. Please check if the file is valid.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavedPlanLoad = (plan) => {
    setIsLoading(true);
    setError('');

    try {
      const planData = plan.data;

      // Load the plan data
      if (planData.shapes) {
        localStorage.setItem('react-pixi-floor-planner-state', JSON.stringify(planData.shapes));
      }
      if (planData.mergedStructures) {
        localStorage.setItem('react-pixi-floor-planner-merged-structures', JSON.stringify(planData.mergedStructures));
      }

      onLoad && onLoad(planData);
      onClose();
    } catch (error) {
      console.error('Error loading saved plan:', error);
      setError('Error loading saved plan. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSavedPlan = (index) => {
    const updatedPlans = savedPlans.filter((_, i) => i !== index);
    localStorage.setItem('saved-floor-plans', JSON.stringify(updatedPlans));
    setSavedPlans(updatedPlans);
  };

  const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'linear-gradient(135deg, #1a1a1a 0%, #404040 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
    fontFamily: 'Arial, sans-serif',
    fontSize: '16px',
    minWidth: '500px',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'hidden',
    animation: 'slideInFromTop 0.3s ease-out'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    fontSize: '24px',
    fontWeight: 'bold'
  };

  const tabContainerStyle = {
    display: 'flex',
    marginBottom: '24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
  };

  const tabStyle = {
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    borderBottom: '2px solid transparent',
    transition: 'all 0.2s ease'
  };

  const activeTabStyle = {
    ...tabStyle,
    color: 'white',
    borderBottomColor: 'white'
  };

  const contentStyle = {
    maxHeight: '400px',
    overflowY: 'auto'
  };

  const fileUploadStyle = {
    border: '2px dashed rgba(255, 255, 255, 0.3)',
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '20px'
  };

  const fileUploadHoverStyle = {
    ...fileUploadStyle,
    borderColor: 'rgba(255, 255, 255, 0.6)',
    background: 'rgba(255, 255, 255, 0.05)'
  };

  const savedPlanStyle = {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const savedPlanHoverStyle = {
    ...savedPlanStyle,
    background: 'rgba(255, 255, 255, 0.15)',
    transform: 'translateY(-2px)'
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px'
  };

  const buttonStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const loadButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    backdropFilter: 'blur(10px)'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.8)'
  };

  const errorStyle = {
    color: '#ff6b6b',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center'
  };

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '40px 20px',
    color: 'rgba(255, 255, 255, 0.6)'
  };

  return (
    <>
      <style>
        {`
          @keyframes slideInFromTop {
            from {
              opacity: 0;
              transform: translate(-50%, -60%);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%);
            }
          }
        `}
      </style>
      <div style={modalStyle} data-load-modal>
        <div style={headerStyle}>
          <FaFolderOpen />
          Load Floor Plan
        </div>
        
        {error && <div style={errorStyle}>{error}</div>}
        
        <div style={tabContainerStyle}>
          <button
            style={activeTab === 'file' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('file')}
          >
            <FaUpload style={{ marginRight: '8px' }} />
            Load from File
          </button>
          <button
            style={activeTab === 'saved' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('saved')}
          >
            <FaHistory style={{ marginRight: '8px' }} />
            Saved Plans ({savedPlans.length})
          </button>
        </div>

        <div style={contentStyle}>
          {activeTab === 'file' && (
            <div>
              <div
                style={selectedFile ? fileUploadHoverStyle : fileUploadStyle}
                onClick={() => document.getElementById('file-input').click()}
              >
                <FaUpload style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.7 }} />
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  {selectedFile ? selectedFile.name : 'Click to select JSON file'}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.7 }}>
                  {selectedFile ? 'File selected successfully' : 'Select a .json file to load your floor plan'}
                </div>
              </div>
              <input
                id="file-input"
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {activeTab === 'saved' && (
            <div>
              {savedPlans.length === 0 ? (
                <div style={emptyStateStyle}>
                  <FaFileAlt style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                  <div style={{ fontSize: '18px', marginBottom: '8px' }}>No saved plans</div>
                  <div style={{ fontSize: '14px' }}>Save a plan with "Save for future use" to see it here</div>
                </div>
              ) : (
                savedPlans.map((plan, index) => (
                  <div
                    key={index}
                    style={savedPlanStyle}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                    onClick={() => handleSavedPlanLoad(plan)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                          {plan.fileName}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.7 }}>
                          Saved: {new Date(plan.savedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSavedPlan(index);
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div style={buttonContainerStyle}>
          <button 
            style={cancelButtonStyle}
            onClick={onClose}
            disabled={isLoading}
          >
            <FaTimes />
            Cancel
          </button>
          {activeTab === 'file' && (
            <button 
              style={loadButtonStyle}
              onClick={handleFileLoad}
              disabled={isLoading || !selectedFile}
            >
              {isLoading ? (
                <>
                  <div style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  Loading...
                </>
              ) : (
                <>
                  <FaUpload />
                  Load Plan
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default LoadModal; 